import { Injectable } from '@angular/core';
import {
  DailyLogOperationInstance,
  StepInstance,
  TimeDuration
} from '../../models/projects/operation.models';
import { cloneDeep } from 'lodash';
import { DateService } from '../date/date.service';
import { OperationService } from '../operations/operation/operation.service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { DelaySaveHelper } from '../../models/app-data/app-data';

@Injectable()
export class StepService {

  constructor(
    private dateService: DateService,
    private operationService: OperationService,
    private timeZoneService: TimeZoneService,
  ) {
  }

  async splitStepsAtTime(
    instance: DailyLogOperationInstance,
    splitTime: PtLuxonDate,
    minuteGap?: boolean,
    delay?: DailyLogOperationInstance,
    removeDelayDurationWhenAdjusting?: boolean,
    originalDelay?: DailyLogOperationInstance
  ) {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances.filter(s => !s.exclude);

    if (!steps) {
      return;
    }

    const stepsToSplit = [];

    for (let i = 0; i < steps.length; i++) {
      steps[i].startTime = PtLuxonDate.assignPtLuxonDate(steps[i].startTime);
      steps[i].endTime = PtLuxonDate.assignPtLuxonDate(steps[i].endTime);

      // point in time steps (start and end the same), or steps with removed times, should not be split
      if (steps[i].startTime.dateTime == null && steps[i].endTime.dateTime == null
      || (steps[i].startTime.dateTime && steps[i].endTime.dateTime && steps[i].startTime.dateTime.toMillis() === steps[i].endTime.dateTime.toMillis())) {
        continue;
      }

      if (this.dateService.timeInRange(steps[i].startTime.dateTime, steps[i].endTime.dateTime, splitTime.dateTime)) {
        if (steps[i].endTime.dateTime !== splitTime.dateTime) {
          stepsToSplit.push(i);
        }
      }
    }

    let incrementer = 0;
    await Promise.all(stepsToSplit.map(async stepPosition => {
      await this.splitStep(instance, splitTime, stepPosition + incrementer, minuteGap, delay, removeDelayDurationWhenAdjusting, originalDelay);
      incrementer++;

      return stepPosition;
    }));
  }

  // return list of deleted ids
  async revertPreviouslyAddedPause(
    operationToPause: DailyLogOperationInstance,
    delay: DailyLogOperationInstance,
    originalDelay: DailyLogOperationInstance,
    delaySaveHelper: DelaySaveHelper): Promise<number[]> {
    // find corresponding old delay pause and delete it
    // stitch together split step if exists, then minus the old pause duration from step end
    // loop through subsequent steps, save temp duration, set start equal to previous step, set end equal to start + duration, set operation end equal to last step end
    // add a new pause with new delay times
    if (!originalDelay || !operationToPause || !operationToPause.pausedTimes || operationToPause.pausedTimes.length === 0
      || !operationToPause.stepCollectionInstance || !operationToPause.stepCollectionInstance.stepInstances
      || operationToPause.stepCollectionInstance.stepInstances.length === 0) {
      return [];
    }

    // instance may have multiple pauses, we only care about the one linked to the current delay being edited
    const originalPauseIndex = operationToPause.pausedTimes.findIndex(e => e.startDate.dateTime.toMillis() === originalDelay.startTime.dateTime.toMillis());

    if (originalPauseIndex === null || originalPauseIndex === -1) {
      return [];
    }

    let removedStepsDailyLogIds = [];
    // remove original pause and take a copy
    const removedPause = cloneDeep(operationToPause.pausedTimes[originalPauseIndex]) as TimeDuration;
    operationToPause.pausedTimes.splice(originalPauseIndex, 1);

    // find split step if it exists, merge them into one
    for (let i = 0; i < operationToPause.stepCollectionInstance.stepInstances.length; i++) {
      // find first 'half' of split step
      if (operationToPause.stepCollectionInstance.stepInstances[i].endTime && operationToPause.stepCollectionInstance.stepInstances[i].endTime.dateTime &&
        operationToPause.stepCollectionInstance.stepInstances[i].endTime.dateTime.toMillis() === removedPause.startDate.dateTime.toMillis()) {
        const secondHalfOfSplitIndex = operationToPause.stepCollectionInstance.stepInstances.findIndex(e => e.group ===
          operationToPause.stepCollectionInstance.stepInstances[i].group && e.number === (operationToPause.stepCollectionInstance.stepInstances[i].number + 1));

        if (secondHalfOfSplitIndex !== null && secondHalfOfSplitIndex !== -1) {
          operationToPause.stepCollectionInstance.stepInstances[i].endTime = operationToPause.stepCollectionInstance.stepInstances[secondHalfOfSplitIndex].startTime;
          operationToPause.stepCollectionInstance.stepInstances[i].endTimeOffset = operationToPause.stepCollectionInstance.stepInstances[secondHalfOfSplitIndex].startTimeOffset;

          const removedStepLogIds = await this.attemptJoinStep(operationToPause, i, secondHalfOfSplitIndex, true);

          if (removedStepLogIds && removedStepLogIds.length !== 0) {
            removedStepsDailyLogIds = removedStepsDailyLogIds.concat(removedStepLogIds);
          }

          // we need to explicitly add this or the pause wont get removed in all cases where it needs to i.e when only moving the delay start before the paused op
          delaySaveHelper.pauseTimeIdsToDelete.push(removedPause.id);

          // todo - re-add this if SJ want step durations to be edited - or delete this if no complaints
          // todo - removal of delay duarion from this step - testing not removing delay time when stitching, and not -re-adding it when splitting again later
          // after steps are joined, the joined step will be index i, now need to remove delay duration from this step
          // operationToPause.stepCollectionInstance.stepInstances[i].endTime.date
          //  = new Date(operationToPause.stepCollectionInstance.stepInstances[i].endTime.date.toMillis() - removedPauseDuration);

          // todo- re-add if readding above, if we aren't altering stitched step end, we dont need to adjust later steps.
          // if (operationToPause.stepCollectionInstance.stepInstances.length > i) {
          //   // if there are more steps
          //   // continue through remaining steps, save temp duration + temp stepDelayedStart
          //   // set start equal to new joined step end time + any delayed start, set end to start + temp duration
          //   for (let j = i + 1; j < operationToPause.stepCollectionInstance.stepInstances.length; j++) {
          //     const tempStepDuration = operationToPause.stepCollectionInstance.stepInstances[j].endTime.date.toMillis()
          //       - operationToPause.stepCollectionInstance.stepInstances[j].startTime.date.toMillis();
          //     let tempStepDelayedStart = 0;
          //
          //     if (j !== 0) {
          //       tempStepDelayedStart = operationToPause.stepCollectionInstance.stepInstances[j].startTime.date.toMillis()
          //         - operationToPause.stepCollectionInstance.stepInstances[j - 1].endTime.date.toMillis()
          //         - removedPauseDuration;
          //     }
          //
          //     operationToPause.stepCollectionInstance.stepInstances[j].startTime.date
          //       = new Date(operationToPause.stepCollectionInstance.stepInstances[j - 1].endTime.date.toMillis() + tempStepDelayedStart);
          //     operationToPause.stepCollectionInstance.stepInstances[j].endTime.date
          //       = new Date(operationToPause.stepCollectionInstance.stepInstances[j].startTime.date.toMillis() + tempStepDuration);
          //     operationToPause.stepCollectionInstance.stepInstances[j].sortTime = operationToPause.stepCollectionInstance.stepInstances[j].startTime.date;
          //   }
          // }
          //
          // operationToPause.endTime = operationToPause.stepCollectionInstance
          //   .stepInstances[operationToPause.stepCollectionInstance.stepInstances.length - 1].endTime;
        }

        break;
      }
    }

    return removedStepsDailyLogIds;
  }

  // rejoin steps that were previously split due to split at end of day - or user manually split them
  // also returns list of daily log ids of any removed steps
  async attemptJoinStepsInOperation(instance: DailyLogOperationInstance): Promise<number[]> {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;
    let removedStepsDailyLogIds = [];

    if (!steps) {
      return;
    }

    for (let x = 0; x < steps.length; x++) {
      const familySteps = steps.filter(p => p.group === steps[x].group && x !== steps.indexOf(p));
      for (let y = 0; y < familySteps.length; y++) {
        const removedStepLogIds = await this.attemptJoinStep(instance, x, steps.indexOf(familySteps[y]), false);

        if (removedStepLogIds && removedStepLogIds.length !== 0) {
          removedStepsDailyLogIds = removedStepsDailyLogIds.concat(removedStepLogIds);
        }
      }
    }
  }

  // returns the second steps' daily log id so it can be easily removed - as it's no longer relevant
  async attemptJoinStep(instance: DailyLogOperationInstance, firstStepPosition: number, secondStepPosition: number, revertingPause?: boolean): Promise<number[]> {
    if (firstStepPosition === null || secondStepPosition === null) {
      return [];
    }

    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return [];
    }

    const firstStep = steps[firstStepPosition];

    if (!firstStep) {
      return [];
    }

    /*if (firstStep.startConfirmed || firstStep.endConfirmed) {
      return;
    }*/

    const secondStep = steps[secondStepPosition];

    if (!secondStep) {
      return [];
    }

    // theres no way that two different steps should have ever need to be joined i dont think
    if (firstStep.step.id != secondStep.step.id) {
      return [];
    }

    /*if (secondStep.startConfirmed || secondStep.endConfirmed) {
      return;
    }*/

    if (!!instance.cancelledTime) {
      const firstStepCancel = firstStep.startTime.dateTime >= instance.cancelledTime;
      const secondStepCancel = secondStep.startTime.dateTime >= instance.cancelledTime;

      if (firstStepCancel !== secondStepCancel) {
        return [];
      }
    }

    const check = this.checkNewStepTimes(firstStep, secondStep);

    if (!check && !revertingPause) {
      return [];
    }

    if (secondStep.group !== firstStep.group) {
      return [];
    }

    const firstIsConcurrent = this.operationService.isTypeAtTimeConcurrent(instance, firstStep.startTime.getDate());
    const secondIsConcurrent = this.operationService.isTypeAtTimeConcurrent(instance, secondStep.startTime.getDate());

    if (firstIsConcurrent !== secondIsConcurrent) {
      return [];
    }

    const firstStepStartDay = await firstStep.startTime.getDay();
    const firstStepEndDay = await firstStep.endTime.getDay();
    const secondStepStartDay = await secondStep.startTime.getDay();
    const secondStepEndDay = await secondStep.endTime.getDay();

    if (firstStepEndDay !== firstStepStartDay
      && firstStepEndDay !== -1
      && (firstStep.endTimeToShow !== '00:00' || firstStep.endTime.dateTime < secondStep.endTime.getDate())) {
      return [];
    }

    if (secondStepStartDay !== firstStepStartDay
      && secondStepStartDay !== -1
      && (secondStep.endTimeToShow !== '00:00' || firstStep.endTime.dateTime > secondStep.endTime.getDate())) {
      return [];
    }

    if (secondStepEndDay !== firstStepStartDay && secondStepEndDay !== -1) {
      return [];
    }

    // don't stitch if there's a gap between steps when on the operation edit page - this gap would hav been caused by a pause
    if (!revertingPause &&
      (firstStep.endTime && firstStep.endTime.dateTime && secondStep.startTime && secondStep.startTime.dateTime) &&
      (firstStep.endTime.dateTime.toMillis() !== secondStep.startTime.dateTime.toMillis()) &&
      (secondStep.endTime.dateTime && secondStep.endTime.dateTime.toMillis() !== firstStep.startTime.dateTime.toMillis())) {
      return [];
    }

    // // don't stitch if there's a gap between steps when on the operation edit page - this gap would hav been caused by a pause
    // if (!revertingPause &&
    //   (firstStep.endTime && firstStep.endTime.date && secondStep.startTime && secondStep.startTime.date) &&
    //   (firstStep.endTime.date.toMillis() !== secondStep.startTime.date.getTime()) ||
    //   // because attemptJoinStep is called multiple times, and we don't know which way around the step positions will be, we need to check both steps
    //   // as below, (i.e we don't know whether the actual first step chronologically is 'firstStep' or 'secondStep', so we need the check below)
    //   (secondStep.endTime && secondStep.endTime.date && firstStep.startTime && firstStep.startTime.date) &&
    //   (secondStep.endTime.date.toMillis() !== firstStep.startTime.date.getTime())) {
    //   return [];
    // }

    const joinedStep = new StepInstance();

    joinedStep.step = cloneDeep(firstStep.step);

    cloneDeep(joinedStep.startTimeOffset)
    if (secondStep.startTime.dateTime > firstStep.startTime.dateTime || !secondStep.startTime.dateTime) {
      joinedStep.startTime = cloneDeep(firstStep.startTime);
      joinedStep.startTimeOffset = cloneDeep(firstStep.startTimeOffset);
      joinedStep.startTimeToShow = cloneDeep(firstStep.startTimeToShow);
      joinedStep.secondsDelay = cloneDeep(firstStep.secondsDelay);
    } else {
      joinedStep.startTime = cloneDeep(secondStep.startTime);
      joinedStep.startTimeOffset = cloneDeep(secondStep.startTimeOffset);
      joinedStep.startTimeToShow = cloneDeep(secondStep.startTimeToShow);
      joinedStep.secondsDelay = cloneDeep(secondStep.secondsDelay);
    }

    if (secondStep.endTime.dateTime > firstStep.endTime.dateTime || !secondStep.startTime.dateTime) {
      joinedStep.endTime = cloneDeep(secondStep.endTime);
      joinedStep.endTimeOffset = cloneDeep(secondStep.endTimeOffset);
      joinedStep.endTimeToShow = cloneDeep(secondStep.endTimeToShow);
    } else {
      joinedStep.endTime = cloneDeep(firstStep.endTime);
      joinedStep.endTimeOffset = cloneDeep(firstStep.endTimeOffset);
      joinedStep.endTimeToShow = cloneDeep(firstStep.endTimeToShow);
    }

    if (!secondStep.endTime.dateTime || !firstStep.endTime.getDate()) {
      joinedStep.endTime.setDate(null);
      joinedStep.endTimeToShow = null;
    }

    if (!!firstStep.comment && !!secondStep.comment) {
      joinedStep.comment = cloneDeep(firstStep.comment);
      joinedStep.comment.data += ' | ' + secondStep.comment.data;
    } else {
      joinedStep.comment = !firstStep.comment ? cloneDeep(secondStep.comment) : cloneDeep(firstStep.comment);
    }

    joinedStep.step.description = firstStep.step.description;
    joinedStep.group = firstStep.group;
    joinedStep.sortTime = joinedStep.startTime.dateTime;
    joinedStep.step.lineTypeName = firstStep.step.lineTypeName;
    joinedStep.step.name = firstStep.step.name;
    joinedStep.stepId = firstStep.stepId;
    joinedStep.jackingEventGuid = firstStep.jackingEventGuid;
    joinedStep.canConfirmTimes = firstStep.canConfirmTimes;

    if (!joinedStep.startTime.dateTime || !joinedStep.endTime.getDate()) {
      joinedStep.secondsSpan = firstStep.secondsSpan;
    } else {
      if (joinedStep.startTime && joinedStep.startTime.dateTime && joinedStep.endTime && joinedStep.endTime.dateTime) {
        joinedStep.secondsSpan = (joinedStep.endTime.dateTime.toMillis() - joinedStep.startTime.dateTime.toMillis()) / 1000;
      }
    }

    joinedStep.isConcurrent = firstStep.isConcurrent;

    const formerStepPosition = firstStepPosition > secondStepPosition ? secondStepPosition : firstStepPosition;
    const latterStepPosition = firstStepPosition > secondStepPosition ? firstStepPosition : secondStepPosition;

    steps.splice(formerStepPosition, 1);

    steps.splice(latterStepPosition - 1, 1, joinedStep);

    for (let i = 0; i < steps.length; i++) {
      steps[i].number = i + 1;
    }

    const result = [];
    // result.push(secondStep.dailyLogRecordId ? secondStep.dailyLogRecordId : secondStep.dailyLogRecordLocalId);
    result.push(firstStep.dailyLogRecordId ? firstStep.dailyLogRecordId : firstStep.dailyLogRecordLocalId);

    return result;
  }

  async splitStep(
    instance: DailyLogOperationInstance,
    splitTime: PtLuxonDate,
    stepPosition: number,
    minuteGap?: boolean,
    delay?: DailyLogOperationInstance,
    removeDelayDurationWhenAdjusting?: boolean,
    originalDelay?: DailyLogOperationInstance
  ) {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return;
    }

    if (!stepPosition && stepPosition !== 0) {
      return;
    }

    if (!splitTime || (splitTime.dateTime && isNaN(splitTime.dateTime.toMillis()))) {
      return;
    }

    const step = steps[stepPosition];

    if (!step) {
      return;
    }

    // a step should never get into this state, but somehow one did, so i'm putting this here as a failsafe to prevent
    // null reference below - (SOS-2068, somehow the point in time step had an end time but no start time)
    // i could change the below if statement to and || instead of &&, but i'm not confident - that might change something else unexpectedly
    // i dont see any problems with nulling out a steps end time if the start time is also null, so this is the solution
    if (!step.startTime.dateTime && step.endTime.dateTime) {
      step.endTime.dateTime = null;
    }

    const stepStartTime = step.startTime.dateTime;
    const stepEndTime = step.endTime.dateTime;

    if (!stepStartTime && !stepEndTime) {
      return;
    }

    // when editing a delay, the first thing that happens is if 2 steps are split, they get joined together, this means that when editing a delay
    // the 'originalStepDuration' will actually be the originalStepDuration PLUS delay duration, which is why we need removeDelayDurationWhenAdjusting later
    const originalStepDuration = !step.endTime || !step.endTime.dateTime
      ? 0
      : step.endTime.dateTime.toMillis() - step.startTime.dateTime.toMillis();
    const firstStepStart = cloneDeep(stepStartTime);
    let firstStepEnd = cloneDeep(splitTime.dateTime);

    if (minuteGap && splitTime && splitTime.dateTime) {
      firstStepEnd = cloneDeep(splitTime.dateTime.minus({minute: 1}));
    } else if (delay) {
      firstStepEnd = cloneDeep(delay.startTime.dateTime);
    }

    const secondStepStart = cloneDeep(delay ? delay.endTime.dateTime : splitTime.dateTime);
    let secondStepEnd = cloneDeep(stepEndTime);

    if (delay && delay.endTime && delay.endTime.dateTime && secondStepEnd) {
      const firstHalfStepDuration = firstStepEnd.toMillis() - firstStepStart.toMillis();
      let difference = originalStepDuration - firstHalfStepDuration;

      if (removeDelayDurationWhenAdjusting && originalDelay && originalDelay.endTime && originalDelay.endTime.dateTime && originalDelay.endTime.dateTime.toMillis()) {
        // if user is editing a delay, without this, every time a delay time is adjusted, the second half of the split will increase not by
        // the adjusted amount but by the entire delay duration
        difference = difference - (originalDelay.endTime.dateTime.toMillis() - originalDelay.startTime.dateTime.toMillis());
      }

      secondStepEnd = delay.endTime.dateTime.plus({millisecond: difference});
    } else if (delay && (!delay.endTime || !delay.endTime.dateTime)) {
      secondStepEnd = null;
    }

    const step1 = new StepInstance();
    step1.id = step.id;
    step1.startTime.setDate(firstStepStart);
    step1.startConfirmed = step.startConfirmed;
    step1.endTime.setDate(firstStepEnd);
    step1.endConfirmed = step.startConfirmed;
    step1.secondsDelay = step.secondsDelay;
    if (step1 && step1.endTime && !!step1.endTime.dateTime && step1.startTime && !!step1.startTime.getDate()) {
      step1.secondsSpan = (step1.endTime.dateTime.toMillis() - step1.startTime.dateTime.toMillis()) / 1000;
    } else {
      step1.secondsSpan = 0;
    }
    step1.group = step.group;
    step1.sortTime = firstStepStart;
    step1.step = cloneDeep(step.step);
    step1.step.name = step.step.name;
    step1.step.lineTypeName = step.step.lineTypeName;
    step1.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step1.startTime);
    step1.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step1.endTime);
    step1.comment = null; // we only want the comment to appear on the second part of the split step;
    step1.step.description = step.step.description;
    step1.step.defaultSecondsSpan = step.step.defaultSecondsSpan;
    step1.number = step.number;
    step1.stepId = step.step.id;
    step1.jackingEventGuid = step.jackingEventGuid;
    step1.isConcurrent = step.isConcurrent;

    step1.startTimeOffset = step.startTimeOffset;
    step1.endTimeOffset = await this.timeZoneService.offsetForTime(step1.endTime.dateTime);

    const step2 = new StepInstance();
    step2.startTime.setDate(secondStepStart);
    step2.startConfirmed = step.endConfirmed;
    step2.endTime.setDate(secondStepEnd);
    step2.endConfirmed = step.endConfirmed;
    step2.secondsDelay = 0;
    if (step2 && step2.endTime && !!step2.endTime.dateTime && step2.startTime && !!step2.startTime.getDate()) {
      step2.secondsSpan = (step2.endTime.dateTime.toMillis() - step2.startTime.dateTime.toMillis()) / 1000;
    } else {
      step2.secondsSpan = 0;
    }
    step2.group = step.group;
    step2.sortTime = secondStepStart;
    step2.step = cloneDeep(step.step);
    step2.step.name = step.step.name;
    step2.step.lineTypeName = step.step.lineTypeName;
    step2.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step2.startTime);
    step2.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step2.endTime);
    step2.comment = cloneDeep(step.comment);
    if (step2.comment) {
      // The second half is a new step, so its comment must also be treated as a new entity.
      // Reusing the original comment Id causes the API to delete and reinsert that comment
      // with a new database Id but its old Guid.
      step2.comment.id = null;
    }
    step2.step.description = step.step.description;
    step2.step.defaultSecondsSpan = step.step.defaultSecondsSpan;
    step2.number = step.number + 1;
    step2.stepId = step.step.id;
    step2.jackingEventGuid = step.jackingEventGuid;
    step2.isConcurrent = step.isConcurrent;

    step2.startTimeOffset = await this.timeZoneService.offsetForTime(step2.startTime.getDate());
    step2.endTimeOffset = await this.timeZoneService.offsetForTime(step2.endTime.getDate());

    for (let i = stepPosition; i < steps.length; i++) {
      steps[i].number++;
    }

    steps.splice(stepPosition, 1, step1, step2);

    // if we are splitting for a delay, we also need to go through and subsequent steps and increase the times
    if (delay && delay.endTime && delay.endTime.dateTime) {
      for (let j = stepPosition + 2; j < steps.length; j++) {
        if (!steps[j].endTime || !steps[j].endTime.dateTime) {
          break;
        }

        const tempStepDuration = steps[j].endTime && steps[j].endTime.dateTime
          ? steps[j].endTime.dateTime.toMillis() - steps[j].startTime.dateTime.toMillis()
          : 0;
        let tempStepDelayedStart = 0;

        if (j !== 0) {
          tempStepDelayedStart = steps[j].step.defaultSecondsDelay * 1000;
        }

        steps[j].startTime.dateTime = steps[j - 1].endTime.dateTime.plus({millisecond: tempStepDelayedStart});
        steps[j].startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(steps[j].startTime);
        steps[j].endTime.dateTime = steps[j].startTime.dateTime.plus({millisecond: tempStepDuration});
        steps[j].endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(steps[j].endTime);

        steps[j].sortTime = steps[j].startTime.dateTime;
      }

      instance.endTime = instance.stepCollectionInstance.stepInstances[instance.stepCollectionInstance.stepInstances.length - 1].endTime;
      instance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.endTime);
    }
  }

  private checkNewStepTimes(firstStep: StepInstance, secondStep: StepInstance): boolean {
    if (!firstStep || !secondStep) {
      return;
    }

    const firstStepStartTime = firstStep.startTime ? firstStep.startTime.dateTime : null;
    const firstStepEndTime = firstStep.endTime ? firstStep.endTime.dateTime : null;
    const secondStepStartTime = secondStep.startTime ? secondStep.startTime.dateTime : null;
    const secondStepEndTime = secondStep.endTime ? secondStep.endTime.dateTime : null;

    if (!firstStepStartTime && !firstStepEndTime) {
      return true;
    }

    if (!secondStepStartTime && !secondStepEndTime) {
      return true;
    }

    // if either step is a point in time step then dont merge them
    if (firstStepStartTime == firstStepEndTime || secondStepStartTime == secondStepEndTime) {
      return false;
    }

    return this.stepTimesShouldMerge(firstStepStartTime, firstStepEndTime, secondStepStartTime, secondStepEndTime);
  }

  private stepTimesShouldMerge(firstStepStartTime, firstStepEndTime, secondStepStartTime, secondStepEndTime) {
    if (this.differenceLessThan(!firstStepStartTime ? null : firstStepStartTime.toMillis(),
      !secondStepEndTime ? null : secondStepEndTime.toMillis(), 60000)) {
      return true;
    }

    if (this.differenceLessThan(!firstStepStartTime ? null : firstStepStartTime.toMillis(),
      !secondStepStartTime ? null : secondStepStartTime.toMillis(), 60000)) {
      return true;
    }

    if (this.differenceLessThan(!firstStepEndTime ? null : firstStepEndTime.toMillis(),
      !secondStepStartTime ? null : secondStepStartTime.toMillis(), 60000)) {
      return true;
    }

    if (this.differenceLessThan(!firstStepEndTime ? null : firstStepEndTime.toMillis(),
      !secondStepEndTime ? null : secondStepEndTime.toMillis(), 60000)) {
      return true;
    }

    return false;
  }

  private differenceLessThan(firstNumber: number, secondNumber: number, difference: number): boolean {
    if (!firstNumber || isNaN(firstNumber)) {
      return true;
    }

    if (!secondNumber || isNaN(secondNumber)) {
      return true;
    }

    if (Math.abs(firstNumber - secondNumber) <= difference) {
      return true;
    } else {
      return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/step/step.service.ts